import { useState } from "react";
import { Trash2, Plus } from "lucide-react";
import InputField from "components/fields/InputField";
import { IntegrationTypeConfiguration } from "../types";
import { KeyValuePair } from "./utils/configFormatConverter";

interface ArrayConfigurationFieldProps {
  config: IntegrationTypeConfiguration;
  value: KeyValuePair[];
  onChange: (value: KeyValuePair[]) => void;
  disabled?: boolean;
  error?: string;
}

const ArrayConfigurationField = ({
  config,
  value,
  onChange,
  disabled = false,
  error,
}: ArrayConfigurationFieldProps) => {
  const keyValuePairs = Array.isArray(value) ? value : [];

  const updateKeyValuePair = (id: string, field: "key" | "value", newValue: string) => {
    const updated = [...keyValuePairs];
    const index = updated.findIndex((p) => p.id === id);
    if (index === -1) return;

    updated[index] = {
      ...updated[index],
      [field]: newValue,
    };
    onChange(updated);
  };

  const addKeyValuePair = () => {
    const newId = `pair-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    onChange([...keyValuePairs, { id: newId, key: "", value: "" }]);
  };

  const removeKeyValuePair = (idToRemove: string) => {
    const updated = keyValuePairs.filter((p) => p.id !== idToRemove);
    onChange(updated);
  };

  return (
    <div className="">
      <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
        {config.name}
        {config.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="space-y-2">
        {keyValuePairs.map((pair) => {
          const pairId = pair?.id || `pair-${config.name}-${keyValuePairs.indexOf(pair)}`;
          return (
            <div key={pairId} className="flex gap-2 items-center">
              <div className="flex-1">
                <InputField
                  id={`${config.name}-key-${pairId}`}
                  label=""
                  value={pair?.key || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateKeyValuePair(pairId, "key", e.target.value)
                  }
                  placeholder="Key"
                  disabled={disabled}
                  state={error ? "error" : undefined}
                />
              </div>
              <div className="flex-1">
                <InputField
                  id={`${config.name}-value-${pairId}`}
                  label=""
                  value={pair?.value || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateKeyValuePair(pairId, "value", e.target.value)
                  }
                  placeholder="Value"
                  disabled={disabled}
                  type={config.is_secure ? "password" : "text"}
                  state={error ? "error" : undefined}
                />
              </div>
              <button
                type="button"
                onClick={() => removeKeyValuePair(pairId)}
                disabled={disabled}
                className="mt-2 p-2 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={addKeyValuePair}
          disabled={disabled}
          className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/20"
        >
          <Plus size={18} />
          Add {config.name}
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default ArrayConfigurationField;

